<template>
	<section class="chest-popup">
		<div class="chest-popup__header">
			<img
				:src="`${pathImages}/chests/chest-img.avif`"
				class="popup__img"
			/>
			<DividerLine />
			<div class="chest-popup__header-text">
				{{ translate("ui.special_chest") }}
			</div>
			<AppIconButton
				class="chest-popup__header-icon"
				:type="IconTypes.CLOSE"
				@click="exitWithoutRewards"
			/>
		</div>
		<div class="chest-popup__rewards" v-if="rewards && rewardsObject">
			<RaggedBoxBackground
				slug="income-info-box-bg"
				:skin="{ stopColor1: '#f2dcb5', stopColor2: '#fff3dd' }"
			/>
			<div class="chest-popup__rewards-first">
				<div class="chest-popup__rewards-item">
					<span>{{ `+${formatNumber(rewardsObject.gems)}` }}</span>
					<AppMainIcon :iconName="ResourceType.GEMS" :iconSize="34" />
				</div>
				<div
					class="chest-popup__rewards-item"
					v-if="rewardsObject[ResourceType.SUPER_CLICKER]"
				>
					<span>
						{{
							`+${formatNumber(rewardsObject[ResourceType.SUPER_CLICKER])}`
						}}
					</span>
					<AppMainIcon
						:iconName="ResourceType.SUPER_CLICKER"
						:iconSize="34"
					/>
				</div>
			</div>
			<div class="chest-popup__rewards-second">
				<div class="chest-popup__rewards-item">
					<span>{{ `+${formatNumber(mutatedCoinsReward)}` }}</span>
					<AppMainIcon
						:iconName="ResourceType.COINS"
						:iconSize="24"
					/>
				</div>
			</div>
		</div>
		<div class="btn-group">
			<AppTextButton
				class="chest-popup__btn"
				:type="StatusTypes.PRIMARY"
				:disabled="!communicator.isRewardedAdAvailable"
				:text="translate('button.watch_ad')"
				@click="playAd"
			/>
			<div
				class="chest-popup__btn-divider"
				v-if="resourcesStore.adSkipper > 0"
			>
				<DecorativeTextDivider :label="translate('ui.or')" />
			</div>
			<AppTextButton
				v-if="resourcesStore.adSkipper > 0"
				class="chest-popup__btn"
				:type="
					resourcesStore.adSkipper
						? StatusTypes.SUCCESS
						: StatusTypes.WARNING
				"
				:text="
					translate(
						resourcesStore.adSkipper
							? 'button.skip_ad'
							: `button.buy_${ResourceType.AD_SKIPPER}`,
					)
				"
				@click="skipAd"
			/>
			<div
				class="chest-popup__skippers"
				v-if="resourcesStore.adSkipper > 0"
			>
				{{ `${translate(`ui.${ResourceType.AD_SKIPPER}_left`)}: ` }}
				<span>{{ formatNumber(resourcesStore.adSkipper) }}</span>
				<span :class="`icon-${ResourceType.AD_SKIPPER}-16`" />
			</div>
		</div>
	</section>
</template>

<script lang="ts" setup>
import AppIconButton from "@/components/AppIconButton.vue";
import AppMainIcon from "@/components/AppMainIcon.vue";
import AppTextButton from "@/components/AppTextButton.vue";
import DecorativeTextDivider from "@/components/SVG/DecorativeTextDivider.vue";
import DividerLine from "@/components/SVG/DividerLine.vue";
import RaggedBoxBackground from "@/components/SVG/RaggedBoxBackground.vue";
import ChestRepository from "@/game/ChestRepository";
import { ResourceType } from "@/game/entities/config/resources";
import { UpgradeIds } from "@/game/entities/config/upgrades";
import type { ChestReward } from "@/game/entities/objects/chests";
import { ToastPlacement } from "@/game/ToastMessage";
import { getUpgradeCurrentReplaceValueById } from "@/game/utils/upgrades";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import useRewardClaimGuard from "@/hooks/useRewardClaimGuard";
import useSocketService from "@/hooks/useSocketService";
import Communicator from "@/libs/AppCommunicatior";
import { AdAnalyticsClickSource, AdAnalyticsType } from "@/libs/SocketService";
import router from "@/router";
import useResourcesStore from "@/store/useResourcesStore";
import { IconTypes, RouteNames, StatusTypes } from "@/types/ui";
import { formatNumber } from "@/utils/format";
import { ceilNumber } from "@/utils/number";
import { translate } from "@/utils/translate";
import { pathImages } from "@/utils/url";
import { computed, markRaw, nextTick, onMounted, ref } from "vue";

const claimGuard = useRewardClaimGuard();

const chestrepo = markRaw(ChestRepository.getInstance());
const communicator = markRaw(Communicator.getInstance());
const socketService = markRaw(useSocketService());

const resourcesStore = useResourcesStore();

const emit = defineEmits(["close"]);

interface Props {
	rewards: string | undefined;
}

const { rewards } = defineProps<Props>();

const initClickable = ref<boolean>(false);

onMounted((): void => {
	setTimeout((): void => {
		initClickable.value = true;
	}, 650);
});

const rewardsObject = computed((): Record<string, number> | null => {
	if (!rewards) return null;
	return JSON.parse(rewards);
});

const mutatedCoinsReward = computed((): number => {
	const baseCoins = (rewardsObject.value?.coins as number) || 0;
	const upgradeBonus =
		1 +
		getUpgradeCurrentReplaceValueById(UpgradeIds.inflation) +
		getUpgradeCurrentReplaceValueById(UpgradeIds.geyser);

	return ceilNumber(baseCoins * upgradeBonus);
});

const playAd = (): Promise<void> =>
	claimGuard.guard((): void => {
		if (!initClickable.value) return;

		if (import.meta.env.DEV) {
			chestrepo.removeActiveSpecialChest(true);
			emit("close");
			return;
		}
		socketService.emitAdAnalyticsSave({
			type: AdAnalyticsType.CHEST_AD,
			clickSource: AdAnalyticsClickSource.POPUP,
		});
		emit("close");
		communicator.playRewardedAd();
	});

const exitWithoutRewards = (): Promise<void> =>
	claimGuard.guard((): void => {
		if (!initClickable.value) return;

		const rewardBase = chestrepo.activeSpecialChestReward as ChestReward;
		const reward: ChestReward = {
			coins: ceilNumber((rewardBase?.coins ?? 0) / 5),
		};
		setTimeout((): void => {
			useEmitter().emit(EmitterEvents.PhaserTileClick, {
				x: 0,
				y: 0,
				reward,
				placement: ToastPlacement.TOP_CENTER,
			});
		}, 200);
		chestrepo.removeActiveSpecialChest(false);
		emit("close");
	});

const skipAd = (): Promise<void> =>
	claimGuard.guard((): void => {
		if (resourcesStore.adSkipper <= 0) {
			chestrepo.removeActiveSpecialChest(false);
			router.push({ name: RouteNames.SHOP });
			emit("close");
			return;
		}
		resourcesStore.decrease(
			ResourceType.AD_SKIPPER,
			1,
			"used for skip chest",
		);
		nextTick((): void => {
			const reward = chestrepo.activeSpecialChestReward as ChestReward;
			if (ChestRepository.getInstance().initialized) {
				ChestRepository.getInstance().removeActiveSpecialChest(true);
			}

			setTimeout((): void => {
				useEmitter().emit(EmitterEvents.PhaserTileClick, {
					x: 0,
					y: 0,
					reward,
					placement: ToastPlacement.TOP_CENTER,
					isSpecialChest: true,
				});
			}, 100);
		});
		emit("close");
	});
</script>

<style lang="scss" scoped>
.chest-popup {
	@include flex-col-center;
	gap: 0.85rem;
	height: 100%;
	&__header {
		position: relative;
		&-icon {
			position: absolute;
			top: 0.75rem;
			right: 0.5rem;
		}
		&-text {
			position: absolute;
			top: 1rem;
			left: 1rem;
			color: #f4d278;
			font-weight: bold;
			font-size: 1.25rem;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
			text-transform: uppercase;
		}
	}

	.btn-group {
		@include flex-col-center;
		gap: 0.5rem;
		height: 100%;
	}

	.popup__img {
		width: 100%;
		object-fit: cover;
	}

	&__rewards {
		position: relative;
		width: 90%;
		height: 6.75rem;
		@include flex-col-center;
		&-item {
			display: flex;
			align-items: center;
			font-size: 1.25rem;
			& > span {
				margin-right: 0.15rem;
				margin-bottom: 0.25rem;
				color: #372615;
				font-weight: bold;
			}
		}
		&-first {
			display: flex;
			gap: 2rem;
			.chest-popup__rewards-item {
				font-size: 1.813rem;
			}
		}
	}
	&__btn {
		width: 15.625rem;
		&-divider {
			& > span {
				color: #372615;
				font-weight: bold;
				font-size: 1.063rem;
			}
		}
	}
	&__skippers {
		display: flex;
		align-items: center;
		gap: 0.15rem;
		color: #372615;
		font-size: 0.906rem;
		& > span {
			font-weight: bold;
		}
	}

	:deep(.ragged-box-bg) {
		position: absolute;
		z-index: -1;
		filter: drop-shadow(0 0 0.1rem rgba(49, 37, 22, 0.6));
		width: 100%;
		height: 100%;
	}

	:deep(.divider-line) {
		position: absolute;
		bottom: 0.15rem;
		left: 0;
		width: 100%;
	}
}
</style>
